import type {
  LocationQuery,
  RouteLocationNamedRaw,
  RouteLocationNormalizedGeneric,
  RouteLocationPathRaw,
  RouteLocationRaw,
  Router,
} from '#vue-router'

const pendingQueryRemovals = new WeakMap<Router, Promise<unknown>>()

export const useRouterUtils = () => {
  const router = useRouter()
  const { back, currentRoute, replace } = router
  const { navigateToLocalized } = useNavigateToLocalized()

  const encodedRedirect = computed(() => redirectQuery.value.redirect)
  const decodedRedirect = computed(() => decodeRedirectRoute(currentRoute.value))
  const redirectQuery = computed(() => currentRoute.value.query)

  const getDecodedQueryParams = (route: RouteLocationNormalizedGeneric) => {
    const encodedQueryParams = ['redirect']

    return encodedQueryParams.reduce(
      (all, param) => {
        if (typeof param !== 'string') {
          return all
        }

        const decodedQuery: LocationQuery = {}

        try {
          const searchParams = new URLSearchParams(
            decodeURIComponent(route.query?.[param] ? String(route.query?.[param]) : ''),
          )
          searchParams.forEach((value, key) => {
            decodedQuery[key] = value
          })
        } catch (e) {
          console.error(`Failed to decode ${param} query parameter:`, e)
          return all
        }

        return R_merge(all, decodedQuery)
      },
      {} as Record<string, any>,
    )
  }

  const navigateBack = async (options?: {
    fallbackRoute?: RouteLocationRaw
    previousRoute?: RouteLocationRaw
  }) => {
    const { previousRoute } = useRoute().meta
    const to = options?.previousRoute || previousRoute

    if (to) {
      await navigateToLocalized(to, { replace: true })
    } else if (window.history.state.back) {
      back()
    } else if (options?.fallbackRoute) {
      await navigateToLocalized(options.fallbackRoute, { replace: true })
    } else {
      await navigateToLocalized('/', { replace: true })
    }
  }

  const navigateToWithRedirect = (
    to: RouteLocationNamedRaw | RouteLocationPathRaw,
    options?: {
      replace?: boolean
    },
  ) => navigateToLocalized(createRedirectLocation(to, encodedRedirect.value), options)

  const navigateToBackroute = () => {
    const backroute = useRoute().query.backroute
    const path = typeof backroute === 'string' ? backroute : '/'
    navigateToLocalized(path, { replace: true })
  }

  const removeQueryParams = (...params: string[]) => {
    const removal = (pendingQueryRemovals.get(router) ?? Promise.resolve()).then(() =>
      replace({
        query: R_omit(currentRoute.value.query, params),
      }),
    )

    pendingQueryRemovals.set(
      router,
      removal.catch(() => undefined),
    )

    return removal
  }

  return {
    decodedRedirect,
    encodedRedirect,
    getDecodedQueryParams,
    navigateBack,
    navigateToLocalized,
    navigateToBackroute,
    navigateToWithRedirect,
    redirectQuery,
    removeQueryParams,
  }
}
